import { ReloadOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import { Form, Switch, Typography } from "antd";
import { ReactNode } from "react";
import ActionButton from "./buttons/ActionButton";
import CancelButton from "./buttons/CancelButton";

export interface SwitchSetting {
  name: string;
  label: string;
  description: string;
}

interface SettingsPanelProps {
  title: string;
  description?: string;
  loading?: boolean;
  onReload: () => void;
  /** Rendered as label + description + switch rows (left column) */
  switches: SwitchSetting[];
  /** Other fields (right column) */
  children?: ReactNode;
}

/**
 * Settings block for an antd `Form`: header with Reload / Save (submit), switches on the left, fields on the right.
 * Must be rendered inside a `<Form>`; Save triggers the form's onFinish.
 */
const SettingsPanel = ({ title, description, loading, onReload, switches, children }: SettingsPanelProps) => (
  <Box
    sx={{
      border: "1px solid var(--app-border)",
      borderRadius: "8px",
      p: 3,
      backgroundColor: "background.paper",
    }}
  >
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
      <Box>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {title}
        </Typography.Title>
        {description && <Typography.Text type="secondary">{description}</Typography.Text>}
      </Box>
      <Box display="flex" gap={1.5}>
        <CancelButton appSize="large" startIcon={<ReloadOutlined />} onClick={onReload}>
          Reload
        </CancelButton>
        <ActionButton appSize="large" type="submit" loading={loading}>
          Save Settings
        </ActionButton>
      </Box>
    </Box>

    <Box display="grid" gap={4} gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}>
      <Box>
        {switches.map(({ name, label, description: hint }) => (
          <Box
            key={name}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            py={1.5}
            sx={{ borderBottom: "1px solid var(--app-border-light)" }}
          >
            <Box>
              <Typography.Text strong>{label}</Typography.Text>
              <br />
              <Typography.Text type="secondary">{hint}</Typography.Text>
            </Box>
            <Form.Item name={name} valuePropName="checked" noStyle>
              <Switch checkedChildren="ON" unCheckedChildren="OFF" />
            </Form.Item>
          </Box>
        ))}
      </Box>
      <Box>{children}</Box>
    </Box>
  </Box>
);

export default SettingsPanel;
